import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Card, CardContent, TextField, Typography } from "@mui/material";
import LoginService from "../../services/loginService";
import GoogleSignIn from "../GoogleSignIn/GoogleSignIn";
import NotificationService from "../Notification/notificationService";
import settings from "../../config/settings";

const LoginCard = () => {
  const [userName, setUserName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [confirmPasswordError, setConfirmPasswordError] =
    useState<boolean>(false);

  const { register } = LoginService();
  const { success, error } = NotificationService();

  const navigate = useNavigate();

  const handleSignup = () => {
    if (formChecks()) {
      register(userName, email, password, confirmPassword).then((result) => {
        if (result) {
          success("Registration success: Please check your email inbox");
          navigate(settings.homePage);
        } else {
          error("Registration failed");
        }
      });
    }
  };

  const formChecks = () => {
    let valid = false;
    if (
      userName !== "" &&
      email !== "" &&
      password !== "" &&
      confirmPassword !== ""
    ) {
      if (!confirmPasswordError && !emailError && !passwordError) {
        valid = true;
      } else {
        error("Email/password is not in the right format.");
      }
    } else {
      error("All text fields are required");
    }

    return valid;
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
        event.target.value
      )
    ) {
      setEmailError(true);
    } else {
      setEmailError(false);
    }
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
        event.target.value
      )
    ) {
      setPasswordError(true);
    } else {
      setPasswordError(false);
    }
  };

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(event.target.value);
  };

  const confirmPasswordCheckError = () => {
    setConfirmPasswordError(
      confirmPassword.length > 0 && confirmPassword !== password
    );
  };

  useEffect(() => {
    confirmPasswordCheckError();
  }, [confirmPassword]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 450,
          paddingLeft: { xs: "0px", md: "50px" },
        }}
      >
        <Card
          sx={{
            padding: { xs: "1rem 0rem", sm: "1rem" },
            borderRadius: "10px",
          }}
          className="shadow-box"
        >
          <CardContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              id="loginCardContainer"
            >
              <Box>
                <Typography
                  id="loginCardTitle"
                  variant="h4"
                  component="div"
                  textAlign="center"
                  sx={{ marginBottom: 2, fontWeight: "700 !important" }}
                >
                  Sign Up
                </Typography>
              </Box>

              <Box>
                <TextField
                  label="User Name"
                  id="filled-basic"
                  variant="filled"
                  fullWidth
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                />
              </Box>
              <Box>
                <TextField
                  label="Email address"
                  id="filled-basic"
                  variant="filled"
                  fullWidth
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                />
              </Box>
              <Box>
                <TextField
                  label="Password"
                  id="filled-basic"
                  variant="filled"
                  fullWidth
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                />
              </Box>
              <Box>
                <TextField
                  label="Confirm Password"
                  id="filled-basic"
                  variant="filled"
                  fullWidth
                  type="password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  error={confirmPasswordError}
                  helperText={
                    passwordError || confirmPasswordError
                      ? "The password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&). Password and confirmation must have the same value."
                      : ""
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSignup();
                    }
                  }}
                />
              </Box>

              <Box>
                <Link to={""} style={{ width: "100%" }} onClick={handleSignup}>
                  <a
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      textAlign: "center",
                    }}
                    className="default-btn move-right"
                  >
                    <span>Sign Up</span>
                  </a>
                </Link>
              </Box>

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2">
                    Already have an account?
                  </Typography>
                  <Link to={"/sign-in"}>
                    <Typography variant="blueBoldText" sx={{ marginLeft: 1 }}>
                      Log in
                    </Typography>
                  </Link>
                </Box>
              </Box>

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 35,
                      height: 35,
                      backgroundColor: "#4663ff",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                    }}
                  >
                    <Typography
                      variant="body1"
                      align="center"
                      sx={{ fontWeight: 700, fontSize: "1.15rem !important" }}
                    >
                      or
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ fontWeight: "700 !important" }}
                >
                  Login with social media
                </Typography>
              </Box>

              <Box>
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                  <GoogleSignIn />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LoginCard;
